import { useEffect, useRef } from "react";

/**
 * Custom React hook that works like useEffect, but provides both the previous and current value
 * of a dependency to the effect callback.
 *
 * @template T - The type of the dependency value.
 * @param {(prev: T | null, curr: T) => void} effect - Effect callback that receives the previous and current value.
 * @param {T} value - The dependency value to watch for changes.
 *
 * @example
 * ```tsx
 * const [name, setName] = useState("name");
 * useEffectWithPrevious((prev, curr) => {
 *   console.log("Name changed from", prev, "to", curr);
 * }, name);
 * ```
 */
export function useEffectWithPrevious<T>(
  effect: (prev: T | null, curr: T) => void,
  value: T,
) {
  const prevRef = useRef<T>(null);

  useEffect(() => {
    effect(prevRef.current, value);
    prevRef.current = value;
  }, [effect, value]);
}
